/**
 * Distributes experiences from a 1-night repeatable package across multiple nights.
 *
 * For repeatable packages:
 * - Day 0 experiences: Occur on check-in through the night before checkout
 * - Day 1 experiences: Occur from the day after check-in through checkout
 */
export function getRepeatableExperienceOccurrenceDays({
  experienceDayNumber,
  stayDuration,
}: {
  experienceDayNumber: number;
  stayDuration: number;
}): number[] {
  const days: number[] = [];

  for (let dayIndex = 0; dayIndex <= stayDuration; dayIndex++) {
    const isDay0Experience = experienceDayNumber === 0;
    const isDay1Experience = experienceDayNumber === 1;
    const isCheckoutDay = dayIndex === stayDuration;

    const shouldInclude =
      (isDay0Experience && !isCheckoutDay) || (isDay1Experience && dayIndex > 0);

    if (shouldInclude) {
      days.push(dayIndex);
    }
  }

  return days;
}

/**
 * Groups experiences by their original day number (0 or 1) for repeatable packages
 */
export function groupRepeatableExperiencesByDay<T extends { dayNumber?: number }>(
  experiences: T[]
): { day0: T[]; day1: T[] } {
  const day0: T[] = [];
  const day1: T[] = [];

  experiences.forEach((exp) => {
    const dayNumber = exp.dayNumber ?? 0;
    if (dayNumber === 0) {
      day0.push(exp);
    } else if (dayNumber === 1) {
      day1.push(exp);
    }
  });

  return { day0, day1 };
}
